import * as THREE from "three";
import { outwardOn, type AsteroidBody } from "@dyson/shared";
import { Flame } from "./ShipScars";

/** How far below a burning tile's middle its flames are rooted, toward the asteroid (so they rise off its surface). */
const ROOT = 0.3;
/** Brush fires are a touch smaller than a burning hull. */
const GROW = 0.85;

/**
 * Brush fires on asteroids: the ship flames (see Flame) on every burning
 * tile, licking straight out from the rock's centre. In world coordinates. Flames are kept for reuse
 * when their fire goes out.
 */
export class GroundFlames {
  readonly group = new THREE.Group();
  /** The flame on each burning tile, by "asteroid id:tile index". */
  private readonly burning = new Map<string, Flame>();
  private readonly spare: Flame[] = [];
  private readonly seen = new Set<string>();
  private seeds = 0;

  update(fires: [asteroidId: string, index: number][], asteroids: Map<string, AsteroidBody>, time: number): void {
    const seen = this.seen;
    seen.clear();
    for (const [id, index] of fires) {
      const a = asteroids.get(id);
      if (!a) continue;
      const key = `${id}:${index}`;
      seen.add(key);
      let flame = this.burning.get(key);
      if (!flame) {
        flame = this.spare.pop() ?? new Flame(this.seeds++ * 7.3);
        const x = a.def.ox + (index % a.grid.w) + 0.5;
        const y = a.def.oy + Math.floor(index / a.grid.w) + 0.5;
        const up = outwardOn(a.def, { x, y });
        // Local +Y turned by `up` points the way the flames rise.
        flame.group.position.set(x + Math.sin(up) * ROOT, y - Math.cos(up) * ROOT, 0.4);
        flame.group.rotation.z = up;
        this.group.add(flame.group);
        this.burning.set(key, flame);
      }
      flame.update(time, GROW);
    }
    for (const [key, flame] of this.burning) {
      if (seen.has(key)) continue;
      this.group.remove(flame.group);
      this.burning.delete(key);
      this.spare.push(flame);
    }
  }
}
